import { inferCategoryTemplate } from "../schemas/activity-blocks";

/**
 * Whether a row is something you can actually reserve, and whether a price
 * belongs on it.
 *
 * `activityDetailSchema` has said since it gained `mode` that the consumer
 * "hides packages / booking CTA / price when mode === 'explore'" — but that
 * was a comment, and every card re-derived the rule or skipped it. On mobile
 * that shipped explore rows with a "Réserver" button that leads nowhere and a
 * price of "0 MAD" (owner, 2026-09-10: "non bookable items dont show the price
 * at all even the 0 MAD"). This is that comment, written once as code.
 */

interface BookableShape {
  /** Absent means bookable — light ActivityDetail builders do not set it.
   *  Plain text is accepted too: some list types carry `mode` as a string. */
  mode?: "bookable" | "explore" | (string & {}) | null;
}

interface PricedShape extends BookableShape {
  price?: number | null;
  /** What the row is, when the caller knows: lets a restaurant be recognised. */
  segment?: string | null;
  template?: string | null;
  categorySlug?: string | null;
  category?: { slug?: string | null; id?: string; name?: string | null } | null;
}

/**
 * A restaurant, bookable or editorial. Restaurants are filed both ways: a
 * bookable row under the `restaurants` category (template `restaurant`), an
 * explore row with the `restaurants` segment (template `explore-restaurant`).
 */
export function isRestaurantListing(a: Omit<PricedShape, "price">): boolean {
  const slug = a.categorySlug ?? a.category?.slug ?? null;
  if (a.segment === "restaurants" || slug === "restaurants") return true;
  const template = inferCategoryTemplate(a.template ?? null, slug, {
    mode: a.mode ?? null,
    segment: a.segment ?? null,
  });
  return template === "restaurant" || template === "explore-restaurant";
}

/** An editorial listing is not bookable; anything else is. */
export function isBookableActivity(a: BookableShape): boolean {
  return (a.mode ?? "bookable") !== "explore";
}

/**
 * A price is shown only when it is both real and ours to charge.
 *
 * Two separate reasons it may not be:
 *  - the row is editorial, so there is nothing to buy at any price;
 *  - the row is bookable but priced at 0, which is how a restaurant booking is
 *    stored — the table is reserved, nothing is paid to us. "0 MAD" reads as
 *    free, which is a different and wrong promise.
 */
export function showsActivityPrice(a: PricedShape): boolean {
  // Restaurant tables are free to reserve: a stored price there is stale data,
  // never something to show (owner rule, 2026-10-30), whatever the amount.
  if (isRestaurantListing(a)) return false;
  return isBookableActivity(a) && (a.price ?? 0) > 0;
}
